import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  RestartAlt,
  AdminPanelSettings,
  Save,
  Cancel,
  Warning,
  CheckCircle,
  Code,
  Assignment,
  AutoAwesome,
  Help
} from '@mui/icons-material'
import axios from 'axios'

const AdminObjetivos = ({ onClose }) => {
  const { user, isAdmin } = useAuth()

  // Log en cada render para debugging
  console.log('AdminObjetivos RENDER - isAdmin:', isAdmin, 'user.rol:', user?.rol)

  const [objetivos, setObjetivos] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [gestiones, setGestiones] = useState([])
  const [plantillas, setPlantillas] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  
  // Estados para diálogos
  const [dialogOpen, setDialogOpen] = useState(false)
  const [resetDialogOpen, setResetDialogOpen] = useState(false)
  const [aiAssistantLoading, setAiAssistantLoading] = useState(false)
  const [aiSuggestion, setAiSuggestion] = useState(null)
  const [aiStatus, setAiStatus] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [plantillasDialogOpen, setPlantillasDialogOpen] = useState(false)
  
  // Estados para el formulario
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    actor_tipo: '',
    gestion_id: '',
    objetivo_descripcion: '',
    categoria: '',
    meta_cuantitativa: '',
    periodo_evaluacion: 'mensual',
    fecha_inicio: '',
    fecha_limite: '',
    responsable_id: '',
    supervisor_id: '',
    observaciones: '',
    tipo_seguimiento: 'manual',
    tabla_seguimiento: '',
    query_seguimiento: '',
    query_manual: ''
  })

  const categorias = ['capacitacion', 'procedimientos', 'auditoria', 'mejora_continua', 'documentacion']
  const periodos = ['semanal', 'quincenal', 'mensual', 'bimestral', 'trimestral']
  const actores = ['administrador', 'editor', 'consultor']

  useEffect(() => {
    console.log('AdminObjetivos - Debug Info:', {
      user: user,
      isAdmin: isAdmin,
      userRol: user?.rol,
      rolComparison: user?.rol === 'administrador',
      typeof_rol: typeof user?.rol
    })
    loadData()
  }, [user])

  const loadData = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      
      // Cargar objetivos
      const objetivosResponse = await axios.get('/api/auditoria/objetivos', {
        headers: { Authorization: `Bearer ${token}` }
      })

      let objetivosData = objetivosResponse.data.objetivos || []

      // Si es editor, filtrar solo objetivos de su gestión
      if (user?.rol === 'editor' && user?.gestion_id) {
        objetivosData = objetivosData.filter(obj => obj.gestion_id === user.gestion_id)
      }

      setObjetivos(objetivosData)
      
      // Cargar usuarios
      const usuariosResponse = await axios.get('/api/auth/usuarios', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setUsuarios(usuariosResponse.data.users || [])
      
      // Cargar gestiones
      const gestionesResponse = await axios.get('/api/gestiones', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setGestiones(gestionesResponse.data || [])
      
      // Cargar plantillas
      const plantillasResponse = await axios.get('/api/auditoria/objetivos/plantillas', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setPlantillas(plantillasResponse.data.plantillas || {})
      
    } catch (error) {
      setError('Error al cargar datos: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const resetFormData = () => {
    setFormData({
      actor_tipo: '',
      gestion_id: user?.rol === 'editor' ? user?.gestion_id || '' : '',
      objetivo_descripcion: '',
      categoria: '',
      meta_cuantitativa: '',
      periodo_evaluacion: 'mensual',
      fecha_inicio: '',
      fecha_limite: '',
      responsable_id: '',
      supervisor_id: '',
      observaciones: '',
      tipo_seguimiento: 'manual',
      tabla_seguimiento: '',
      query_seguimiento: '',
      query_manual: ''
    })
    setEditingId(null)
    setAiSuggestion(null) // Limpiar sugerencia de IA
    setAiStatus('') // Limpiar estado de IA
  }

  const handleOpenDialog = (objetivo = null) => {
    if (objetivo) {
      // Debug: Ver qué campos están llegando
      console.log('Datos del objetivo para editar:', objetivo)

      setFormData({
        actor_tipo: objetivo.actor_tipo || '',
        gestion_id: objetivo.gestion_nombre ? objetivo.gestion_id : '',
        objetivo_descripcion: objetivo.objetivo_descripcion || '',
        categoria: objetivo.categoria || '',
        meta_cuantitativa: objetivo.meta_cuantitativa || '',
        periodo_evaluacion: objetivo.periodo_evaluacion || 'mensual',
        fecha_inicio: objetivo.fecha_inicio || '',
        fecha_limite: objetivo.fecha_limite || '',
        responsable_id: objetivo.responsable_id || '',
        supervisor_id: objetivo.supervisor_id || '',
        observaciones: objetivo.observaciones || '',
        tipo_seguimiento: objetivo.tipo_seguimiento || 'manual',
        tabla_seguimiento: objetivo.tabla_seguimiento || '',
        query_seguimiento: objetivo.query_seguimiento || '',
        query_manual: objetivo.query_manual || ''
      })
      setEditingId(objetivo.id)
    } else {
      resetFormData()
    }
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('sgc_token')

      // Preparar datos - convertir strings vacíos a null para campos numéricos
      const submitData = {
        ...formData,
        gestion_id: formData.gestion_id === '' ? null : formData.gestion_id,
        responsable_id: formData.responsable_id === '' ? null : formData.responsable_id,
        supervisor_id: formData.supervisor_id === '' ? null : formData.supervisor_id,
        tabla_seguimiento: formData.tabla_seguimiento === '' ? null : formData.tabla_seguimiento,
        query_seguimiento: formData.query_seguimiento === '' ? null : formData.query_seguimiento,
        query_manual: formData.query_manual === '' ? null : formData.query_manual
      }

      if (editingId) {
        // Actualizar
        await axios.put(`/api/auditoria/objetivos/${editingId}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Objetivo actualizado exitosamente')
      } else {
        // Crear
        await axios.post('/api/auditoria/objetivos', submitData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Objetivo creado exitosamente')
      }
      
      setDialogOpen(false)
      resetFormData()
      await loadData()
      
    } catch (error) {
      setError('Error al guardar objetivo: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleDelete = async (objetivoId) => {
    try {
      const token = localStorage.getItem('sgc_token')
      
      await axios.delete(`/api/auditoria/objetivos/${objetivoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      setSuccess('Objetivo eliminado exitosamente')
      setDeleteDialogOpen(false)
      await loadData()
      
    } catch (error) {
      setError('Error al eliminar objetivo: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleResetAll = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      
      const response = await axios.post('/api/auditoria/objetivos/resetear-todos', {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      setSuccess(response.data.message)
      setResetDialogOpen(false)
      await loadData()
      
    } catch (error) {
      setError('Error al resetear objetivos: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleResetSingle = async (objetivoId) => {
    try {
      const token = localStorage.getItem('sgc_token')

      await axios.post(`/api/auditoria/objetivos/${objetivoId}/resetear`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setSuccess('Objetivo reseteado exitosamente')
      await loadData()

    } catch (error) {
      setError('Error al resetear objetivo: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleGenerateQueryWithAI = async () => {
    if (!formData.objetivo_descripcion) {
      setError('Ingresa una descripción del objetivo primero')
      return
    }

    try {
      setAiAssistantLoading(true)
      setAiStatus('Analizando objetivo...')
      const token = localStorage.getItem('sgc_token')

      // Simular pasos del proceso para feedback visual
      setTimeout(() => setAiStatus('Consultando tablas disponibles...'), 1000)
      setTimeout(() => setAiStatus('Enviando consulta a la IA...'), 2000)
      setTimeout(() => setAiStatus('Generando query SQL...'), 5000)

      const response = await axios.post('/api/auditoria/objetivos/generar-query', {
        descripcion_objetivo: formData.objetivo_descripcion,
        meta_cuantitativa: formData.meta_cuantitativa,
        tabla_seguimiento: formData.tabla_seguimiento,
        tipo_seguimiento: formData.tipo_seguimiento
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000 // 2 minutos timeout
      })

      setAiStatus('Procesando respuesta...')
      const suggestion = response.data
      setAiSuggestion(suggestion)

      // Poblar AMBOS campos según el tipo de seguimiento
      setFormData({
        ...formData,
        tabla_seguimiento: suggestion.tabla_recomendada,
        query_seguimiento: suggestion.query_sql, // Siempre poblar query automática
        query_manual: suggestion.query_sql       // Siempre poblar query manual
      })

      setAiStatus('')
      setSuccess('¡Query generada con IA! Revisa los campos y haz clic en "Guardar" para aplicar los cambios.')

    } catch (error) {
      setAiStatus('')
      setError('Error al generar query con IA: ' + (error.response?.data?.detail || error.message))
    } finally {
      setAiAssistantLoading(false)
    }
  }

  const handleApplyTemplate = (template) => {
    setFormData({
      ...formData,
      ...template
    })
    setPlantillasDialogOpen(false)
  }

  const getUsuarioNombre = (usuarioId) => {
    const usuario = usuarios.find(u => u.id === usuarioId)
    return usuario ? usuario.nombre : `Usuario ${usuarioId}`
  }

  const getGestionNombre = (gestionId) => {
    const gestion = gestiones.find(g => g.id === gestionId)
    return gestion ? gestion.nombre : `Gestión ${gestionId}`
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando administración de objetivos...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AdminPanelSettings sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            {isAdmin ? 'Administración de Objetivos' : 'Mis Objetivos de Auditoría'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(() => {
            console.log('AdminObjetivos - Botones header - isAdmin:', isAdmin, 'user.rol:', user?.rol)
            return isAdmin
          })() && (
            <>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                color="primary"
              >
                Nuevo Objetivo
              </Button>
              <Button
                variant="outlined"
                startIcon={<RestartAlt />}
                onClick={() => setResetDialogOpen(true)}
                color="warning"
              >
                Resetear Todos
              </Button>
            </>
          )}
          <Button onClick={onClose} variant="outlined">
            Cerrar
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Lista de objetivos */}
      <Grid container spacing={2}>
        {objetivos.map((objetivo) => (
          <Grid item xs={12} md={6} lg={4} key={objetivo.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Chip
                    label={objetivo.actor_tipo}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                  <Chip
                    label={objetivo.tipo_seguimiento === 'automatico' ? 'Automático' : 'Manual'}
                    color={objetivo.tipo_seguimiento === 'automatico' ? 'success' : 'info'}
                    size="small"
                  />
                </Box>

                <Typography variant="h6" gutterBottom sx={{ minHeight: 48 }}>
                  {objetivo.objetivo_descripcion}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Responsable:</strong> {objetivo.responsable.nombre}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Meta:</strong> {objetivo.meta_cuantitativa}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Progreso:</strong> {objetivo.progreso_porcentaje}%
                </Typography>

                <Chip
                  label={objetivo.estado}
                  color={
                    objetivo.estado === 'completado' ? 'success' :
                    objetivo.estado === 'en_progreso' ? 'info' :
                    objetivo.estado === 'vencido' ? 'error' : 'warning'
                  }
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Box>
                  {isAdmin && (
                    <>
                      <Tooltip title="Editar objetivo">
                        <IconButton size="small" onClick={() => handleOpenDialog(objetivo)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar objetivo">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingId(objetivo.id)
                            setDeleteDialogOpen(true)
                          }}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>

                {(() => {
                  console.log('AdminObjetivos - Botón reset individual - isAdmin:', isAdmin, 'user.rol:', user?.rol, 'objetivo.id:', objetivo.id)
                  return isAdmin
                })() && (
                  <Tooltip title="Resetear progreso">
                    <span>
                      <Button
                        size="small"
                        startIcon={<RestartAlt />}
                        onClick={() => handleResetSingle(objetivo.id)}
                        disabled={objetivo.progreso_porcentaje === 0}
                      >
                        Reset
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog para crear/editar objetivo */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {editingId ? <Edit /> : <Add />}
            <Typography variant="h6">
              {editingId ? 'Editar Objetivo' : 'Nuevo Objetivo'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Actor</InputLabel>
                <Select
                  value={formData.actor_tipo}
                  label="Tipo de Actor"
                  onChange={(e) => setFormData({...formData, actor_tipo: e.target.value})}
                >
                  {actores.map(actor => (
                    <MenuItem key={actor} value={actor} sx={{ textTransform: 'capitalize' }}>
                      {actor}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Gestión</InputLabel>
                <Select
                  value={formData.gestion_id}
                  label="Gestión"
                  onChange={(e) => setFormData({...formData, gestion_id: e.target.value})}
                  disabled={!isAdmin}
                >
                  {isAdmin && <MenuItem value="">Ninguna</MenuItem>}
                  {gestiones
                    .filter(gestion =>
                      isAdmin || gestion.id === user?.gestion_id
                    )
                    .map(gestion => (
                      <MenuItem key={gestion.id} value={gestion.id}>
                        {gestion.nombre}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  label="Descripción del Objetivo"
                  multiline
                  rows={3}
                  value={formData.objetivo_descripcion}
                  onChange={(e) => {
                    setFormData({...formData, objetivo_descripcion: e.target.value})
                    // Limpiar sugerencia de IA cuando cambia la descripción
                    if (aiSuggestion) {
                      setAiSuggestion(null)
                      setAiStatus('')
                    }
                  }}
                />
                <Button
                  startIcon={<Assignment />}
                  onClick={() => setPlantillasDialogOpen(true)}
                  variant="outlined"
                  sx={{ minWidth: 120 }}
                >
                  Plantillas
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Categoría</InputLabel>
                <Select
                  value={formData.categoria}
                  label="Categoría"
                  onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                >
                  {categorias.map(cat => (
                    <MenuItem key={cat} value={cat} sx={{ textTransform: 'capitalize' }}>
                      {cat.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Meta Cuantitativa"
                value={formData.meta_cuantitativa}
                onChange={(e) => setFormData({...formData, meta_cuantitativa: e.target.value})}
                placeholder="ej: 15 procedimientos, 100% implementación"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Período de Evaluación</InputLabel>
                <Select
                  value={formData.periodo_evaluacion}
                  label="Período de Evaluación"
                  onChange={(e) => setFormData({...formData, periodo_evaluacion: e.target.value})}
                >
                  {periodos.map(periodo => (
                    <MenuItem key={periodo} value={periodo} sx={{ textTransform: 'capitalize' }}>
                      {periodo}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Fecha de Inicio"
                type="date"
                value={formData.fecha_inicio}
                onChange={(e) => setFormData({...formData, fecha_inicio: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Fecha Límite"
                type="date"
                value={formData.fecha_limite}
                onChange={(e) => setFormData({...formData, fecha_limite: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Responsable</InputLabel>
                <Select
                  value={formData.responsable_id}
                  label="Responsable"
                  onChange={(e) => setFormData({...formData, responsable_id: e.target.value})}
                >
                  {usuarios.map(usuario => (
                    <MenuItem key={usuario.id} value={usuario.id}>
                      {usuario.nombre} ({usuario.rol})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Supervisor</InputLabel>
                <Select
                  value={formData.supervisor_id}
                  label="Supervisor"
                  onChange={(e) => setFormData({...formData, supervisor_id: e.target.value})}
                >
                  <MenuItem value="">Ninguno</MenuItem>
                  {usuarios.filter(u => u.rol === 'administrador').map(usuario => (
                    <MenuItem key={usuario.id} value={usuario.id}>
                      {usuario.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observaciones"
                multiline
                rows={2}
                value={formData.observaciones}
                onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Configuración de Seguimiento
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Seguimiento</InputLabel>
                <Select
                  value={formData.tipo_seguimiento}
                  label="Tipo de Seguimiento"
                  onChange={(e) => setFormData({...formData, tipo_seguimiento: e.target.value})}
                >
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="automatico">Automático</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tabla de Seguimiento"
                value={formData.tabla_seguimiento}
                onChange={(e) => setFormData({...formData, tabla_seguimiento: e.target.value})}
                placeholder="ej: procedimientos, auditoria"
              />
            </Grid>

            {/* Botón Asistente IA */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesome />}
                  onClick={handleGenerateQueryWithAI}
                  disabled={aiAssistantLoading || !formData.objetivo_descripcion}
                  color="secondary"
                  sx={{ minWidth: 200 }}
                >
                  {aiAssistantLoading ? 'Generando...' : 'Asistente IA'}
                </Button>
                {aiStatus && (
                  <Typography variant="body2" color="primary" sx={{ fontStyle: 'italic', ml: 2 }}>
                    🤖 {aiStatus}
                  </Typography>
                )}
                <Tooltip title="El asistente IA analizará tu objetivo y sugerirá la query SQL y tabla apropiadas. Podrás revisar y modificar antes de guardar.">
                  <Help color="action" />
                </Tooltip>
              </Box>
            </Grid>

            {/* Mostrar sugerencia de IA */}
            {aiSuggestion && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>✨ Sugerencia del Asistente IA</strong>
                  </Typography>
                  <Typography variant="body2" gutterBottom sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    Los campos se han actualizado con las sugerencias. Puedes modificarlos antes de guardar.
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Explicación:</strong> {aiSuggestion.explicacion}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Tabla recomendada:</strong> {aiSuggestion.tabla_recomendada}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Query generada:</strong> <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', fontSize: '0.875rem' }}>{aiSuggestion.query_sql}</code>
                  </Typography>
                  {aiSuggestion.sugerencias && aiSuggestion.sugerencias.length > 0 && (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>Sugerencias adicionales:</strong>
                      </Typography>
                      <List dense>
                        {aiSuggestion.sugerencias.map((sugerencia, index) => (
                          <ListItem key={index} sx={{ py: 0 }}>
                            <ListItemText primary={`• ${sugerencia}`} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Alert>
              </Grid>
            )}

            {formData.tipo_seguimiento === 'automatico' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Query Automática"
                  multiline
                  rows={3}
                  value={formData.query_seguimiento}
                  onChange={(e) => setFormData({...formData, query_seguimiento: e.target.value})}
                  placeholder="SELECT COUNT(*) FROM tabla WHERE condicion = {responsable_id}"
                  InputProps={{
                    startAdornment: <Code sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
            )}

            {formData.tipo_seguimiento === 'manual' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Query Manual"
                  multiline
                  rows={3}
                  value={formData.query_manual}
                  onChange={(e) => setFormData({...formData, query_manual: e.target.value})}
                  placeholder="SELECT COUNT(*) FROM tabla WHERE condicion = {responsable_id}"
                  InputProps={{
                    startAdornment: <Code sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} startIcon={<Cancel />}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} variant="contained" startIcon={<Save />}>
            {editingId ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para confirmar reset de todos */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Warning color="warning" />
            <Typography variant="h6">Resetear Todos los Objetivos</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que quieres resetear el progreso de TODOS los objetivos a 0% y estado pendiente?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleResetAll} variant="contained" color="warning" startIcon={<RestartAlt />}>
            Resetear Todos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para confirmar eliminación */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Warning color="error" />
            <Typography variant="h6">Eliminar Objetivo</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que quieres eliminar este objetivo? 
            También se eliminarán todas sus verificaciones asociadas.
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={() => handleDelete(editingId)} 
            variant="contained" 
            color="error" 
            startIcon={<Delete />}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de plantillas */}
      <Dialog open={plantillasDialogOpen} onClose={() => setPlantillasDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Assignment />
            <Typography variant="h6">Plantillas de Objetivos</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {Object.entries(plantillas).map(([tipoActor, templates]) => (
            <Box key={tipoActor} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, textTransform: 'capitalize' }}>
                {tipoActor}
              </Typography>
              <List>
                {templates.map((template, index) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={template.objetivo_descripcion}
                      secondary={`${template.categoria} - ${template.meta_cuantitativa} - ${template.tipo_seguimiento}`}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        onClick={() => handleApplyTemplate(template)}
                        startIcon={<CheckCircle />}
                      >
                        Aplicar
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlantillasDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AdminObjetivos